import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import DatePicker from "react-datepicker";
import { saveDirectDebitStartDate, saveDirectDebitFrequency, saveDirectDebitAmount } from "../../ducks/redux/actions/userChoices";
import BackModal from "../Modal/BackModal";
import "./index.scss";
import "react-datepicker/dist/react-datepicker.css";
import * as api from "../../api";
import { getEnvScopedKey } from "../../storybook/Header";

interface SetupDirectDebitForm extends RouteComponentProps<any> {}


const FREQUENCY_CODE_MAP: { [key: string]: string } = {
  '1': '1', '2': '2', '3': '3', '4': '4',
  'Weekly': '1', 'Fortnightly': '2', '4 weekly': '3',
  'Monthly': '4', 'Week': '1', 'Month': '4'
};


const PERIODS_PER_YEAR = { '1': 52, '2': 26, '3': 13, '4': 12 };

const formatDateToYmd = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const SetupDirectDebitForm: React.FC<SetupDirectDebitForm> = (props) => {
  const dispatch = useDispatch();
  const payRef = useSelector((state: any) => state.userChoicesReducer?.accountnumber);
  const rentInfo = useSelector((state: any) => state.financialReferralReducer?.rentDetailsInfo);

  const isLeaseHolder = (useSelector((state: any) => state?.userChoicesReducer?.selectedTenancyGroup)==="Leasehold");
  const rentamount = rentInfo?.['rent-details']?.['rent-amount']?.value || 0;
  const serviceChargeAmount = rentInfo?.['rent-details']?.['service-charge-amount']?.value || 0;
  const amount = (typeof rentamount === 'string' ? Number.parseFloat(rentamount.replaceAll(',', '')) : rentamount) + (typeof serviceChargeAmount === 'string' ? Number.parseFloat(serviceChargeAmount.replaceAll(',', '')) : serviceChargeAmount);
  const defaultFrequency = rentInfo?.["rent-details"]?.["charge-frequency"];
  const defaultFrequencyCode = defaultFrequency ? (FREQUENCY_CODE_MAP[defaultFrequency as string] || defaultFrequency) : null;
  const initialFrequency = isLeaseHolder ? '4' : (defaultFrequencyCode || '4');
  const parsedAmount = typeof amount === 'string' ? Number.parseFloat(amount) : amount;
  const [selectedDate, setSelectedDate] = useState<Date | null>();
  const getInitialAmount = (): number => {
    if (!isLeaseHolder || !defaultFrequencyCode || !parsedAmount) return parsedAmount;

    if (defaultFrequencyCode === '4') return parsedAmount; 

    const fromRate = PERIODS_PER_YEAR[defaultFrequencyCode as keyof typeof PERIODS_PER_YEAR];
    const toRate = PERIODS_PER_YEAR['4' as keyof typeof PERIODS_PER_YEAR]; 

    if (!fromRate || !toRate) return parsedAmount;

    return Math.round((parsedAmount * fromRate / toRate) * 100) / 100;
  };
  const DDToggle = useSelector((state: any) => state.userChoicesReducer?.directDebitToggle);
  const baseAmount = getInitialAmount();
  const [leadWorkingDays, setLeadWorkingDays] = useState("0");

const getNextWorkingDays = (start: Date, count: number): Date[] =>{
  const result: Date[] = [];
  const d = new Date(start);
  d.setHours(0, 0, 0, 0);
  while (result.length < count) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) {
      result.push(new Date(d));
    }
    d.setDate(d.getDate() + 1);
  }
 
  return result;
}
const disabledWorkingDays = useMemo(() => {
    const leadDays = Number.parseInt(leadWorkingDays, 10);
    return getNextWorkingDays(new Date(), Number.isNaN(leadDays) ? 0 : leadDays);
  }, [leadWorkingDays]);

  // Last working day in the lead block — every calendar day up to this is blocked
  const lastLeadDay = useMemo(() => {
    if (disabledWorkingDays.length === 0) return null;
    return disabledWorkingDays.at(-1);
  }, [disabledWorkingDays]);
  const [form, setForm] = useState({
    startDate: "",
    frequency: initialFrequency,
    amount: baseAmount,
    paymentReference: payRef
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [frequencyMap, setFrequencyMap] = useState<{ [key: string]: string }>({});
  const [showBackModal, setShowBackModal] = useState(false);
  const getLeadWorkingDaysFromAppConfigure = async () => {
    try {
      const leadWorkingDaysKey = getEnvScopedKey('MyPeabody:directDebitMinLeadWorkingDays');
      const response = await api.GetAppConfigToggleValue(leadWorkingDaysKey);
      setLeadWorkingDays(response?.value);
    } catch (error) {
      console.error("Failed to fetch get app config days:", error);
    }
  }

  const getFrequencyCode = useCallback((freq: any): string => {
    return FREQUENCY_CODE_MAP[freq as string] || freq;
  }, []);

  const calculateAmount = useCallback((frequency: string): number => {
    const frequencyCode = getFrequencyCode(frequency);
    const baseFrequencyCode = getFrequencyCode(initialFrequency);

    if (!baseAmount || baseAmount === 0 || baseFrequencyCode === frequencyCode) {
      return baseAmount;
    }

    const fromRate = PERIODS_PER_YEAR[baseFrequencyCode as keyof typeof PERIODS_PER_YEAR];
    const toRate = PERIODS_PER_YEAR[frequencyCode as keyof typeof PERIODS_PER_YEAR];

    if (!fromRate || !toRate) return baseAmount;

    return Math.round((baseAmount * fromRate / toRate) * 100) / 100;
  }, [baseAmount, initialFrequency, getFrequencyCode]);

  useEffect(() => {
    if (DDToggle){
    (async () => {
      try {
        await getLeadWorkingDaysFromAppConfigure()
        const frequencyData = await api.getAccountsReferenceData();
        const keyValues = frequencyData[0]?.namespace?.keyValues;

        if (keyValues) {
          const dataToSet = Array.isArray(keyValues)
            ? Object.fromEntries(keyValues.map((item: any) => [item.key || item.id, item.value]))
            : keyValues;

          if (Object.keys(dataToSet).length > 0) {
            setFrequencyMap(dataToSet);
          }
        }
      } catch (e: any) {
        console.error("Error loading reference data:", e);
      }
    })();
    }else {
      props.history.push("/customer360View")
    }
  }, []);

  useEffect(() => {
    dispatch(saveDirectDebitStartDate(form.startDate));
    dispatch(saveDirectDebitFrequency(form.frequency));
    dispatch(saveDirectDebitAmount(Number(form.amount).toFixed(2)));
  }, [form.frequency, form.startDate, form.amount, dispatch]);

  const filterDate = (date: Date) => {
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const isPastDate = normalizedDate < today;
    const isWithinLeadBlock = lastLeadDay
      ? normalizedDate <= lastLeadDay
      : false;

    if (isLeaseHolder) {
      return (
        normalizedDate.getDate() === 1 &&
        !isPastDate &&
        !isWithinLeadBlock
      );
    }
    if (normalizedDate.getTime() === today.getTime()) {
      return false;
    }

    return !isPastDate && !isWithinLeadBlock;
  };

  
  const handleChange = useCallback((e: any) => {
    const { name, value } = e.target;
    if (name === "frequency") {
      const newAmount = calculateAmount(value);
      setForm(prev => ({ ...prev, [name]: value, amount: newAmount }));
      dispatch(saveDirectDebitAmount(Number(newAmount).toFixed(2)));
    }
  }, [calculateAmount, dispatch]);

  const handleDateChange = useCallback((date: Date | null) => {
    if (!date) return;

    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);

    setSelectedDate(normalizedDate);
    setForm(prev => ({ ...prev, startDate: formatDateToYmd(normalizedDate) }));
    setErrors(prev => ({ ...prev, startDate: "" }));
  }, []);

  const minSelectableDate = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const maxSelectableDate = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date(today);
    maxDate.setMonth(maxDate.getMonth() + 6);
    return maxDate;
  }, []);

  const handleSubmit = useCallback((e: any) => {
    e.preventDefault();
    const newErrors: { [key: string]: string } = {};

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    props.history.push("/directdebit");
  }, [form.amount, payRef, props.history]);

  const isFormValid = useMemo(() =>
    form.startDate && payRef && !errors.startDate && !errors.amount,
    [form.startDate, payRef, errors.startDate, errors.amount]
  );

  const callReturnToSetupPageHandler = useCallback(() => {
    props.history.goBack();
  }, [props.history]);

  const handleGoBack = () => {
        setShowBackModal(true)
  };

  return (
    <div className="setup-dd-form-card">
      {showBackModal&&<div className="flex justify-between items-center">
                      <BackModal
                        id="reploc-backmodal"
                        back={callReturnToSetupPageHandler}
                        btn_msg="return_case_button"
                        showExtraButtons={false}
                        isOpen={showBackModal}
                        setIsOpen={setShowBackModal}
                      /></div>}
      <form className="setup-dd-form" onSubmit={handleSubmit}>

        {isLeaseHolder && (
          <div className="info-message">
            Your direct debit must be paid on the 1st of the month
            and in full in line with your agreement.
          </div>
        )}

        <label htmlFor="startDate" className="mb-2">Start Date</label>
        <div className="date-picker-with-icon">
          <DatePicker
            id="startDate"
            selected={selectedDate}
            onChange={handleDateChange}
            dateFormat="yyyy-MM-dd"
            minDate={minSelectableDate}
            maxDate={maxSelectableDate}
            filterDate={filterDate}
            className="date-picker-input"
            wrapperClassName="date-picker-wrapper"
            popperPlacement="bottom-end"
            placeholderText="Select a valid date"
          />
          <i className="input-group-text fa fa-calendar calendar" aria-hidden="true"></i>
        </div>
        {errors.startDate && <span>{errors.startDate}</span>}

        <label htmlFor="frequency" className="mb-2 ">Frequency</label>
    {  isLeaseHolder ?(  <input
  id="frequency"
  type="text"
  value="Monthly"
  readOnly
/>
 ):(
        
        <select
          id="frequency"
          name="frequency"
          value={form.frequency}
          onChange={handleChange}
          disabled={isLeaseHolder}
          data-testid="frequency-select"
        >
            
              {Object.entries(frequencyMap).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            
        </select>)}

        <label htmlFor="amount" className="mb-2">Amount</label>
        <input
          id="amount"
          type="number"
          name="amount"
          value={Number(form.amount).toFixed(2)}
          readOnly
        />
        {errors.amount && <span>{errors.amount}</span>}

        <label htmlFor="paymentReference" className="mb-2">Payment reference</label>
        <input id="paymentReference" value={payRef || ""} readOnly />
        {errors.reference && <span>{errors.reference}</span>}

        <div className="setup-dd-form-actions">
          <button type="button" className="secondary-ghost-btn" onClick={handleGoBack}>
            Go Back
          </button>
          <button type="submit" className="primary-btn" disabled={!isFormValid}>
              Create Direct Debit
          </button>
        </div>
      </form>
    </div>
  );
};

export { SetupDirectDebitForm };